import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { ApiService } from 'app/shared/apis/api.service';
import { StorageKeysService } from 'app/shared/services/storage-keys.service';
import { SERVICE_SHARE_TOKEN_KEY } from 'app/services/service-share.service';
import { DriverSimulationService } from 'app/services/driver-simulation.service';
import { catchError, Observable, switchMap, throwError, map } from 'rxjs';
let versaoJWT;

function getShareToken(): string | null {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SERVICE_SHARE_TOKEN_KEY) : null;
}

/** Páginas públicas de convidado (acompanhamento e teleconsulta do paciente): nunca forçam logout/refresh. */
function isOnSharePage(): boolean {
    if (typeof location === 'undefined') {
        return false;
    }
    const path = location.pathname;
    return path.includes('/acompanhar/') || path.includes('/teleconsulta/');
}

function withDriverSimulationParam(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const driverSimulation = inject(DriverSimulationService);
    const simulatedId = driverSimulation.getSimulatedUserId();
    if (!simulatedId || !req.url.includes('/driver/')) {
        return req;
    }
    let params = req.params ?? new HttpParams();
    if (!params.has('simulate_driver_user_id')) {
        params = params.set('simulate_driver_user_id', String(simulatedId));
    }
    return req.clone({ params });
}

function isOnAuthPage(): boolean {
    if (typeof location === 'undefined') {
        return false;
    }
    const path = location.pathname.toLowerCase();
    return (
        path.includes('/sign-in') ||
        path.includes('/sign-up') ||
        path.includes('/forgot-password') ||
        path.includes('/reset-password') ||
        path.includes('/confirmation-required') ||
        path.includes('/not-found-company')
    );
}

/** Endpoints que o ShareOrAuthGuard / ShareSessionGuard aceitam. */
function isShareAllowedApiUrl(url: string): boolean {
    return (
        url.includes('/public/service-share/session/') ||
        url.includes('/service/active') ||
        url.includes('/video_equipment/') ||
        /\/vehicle\/\d+/.test(url)
    );
}

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const apiService = inject(ApiService);
    const _storageKeysService = inject(StorageKeysService);

    // Clone the request object
    let newReq = req.clone();

    const shareToken = getShareToken();
    const onSharePage = isOnSharePage();
    const isSharePublicEndpoint =
        req.url.includes('/public/service-share/') &&
        !req.url.includes('/public/service-share/session/');

    let isAuthenticated = authService.token && !AuthUtils.isTokenExpired(authService.token);

    // Na página pública: só anexa JWT de share em rotas que o aceitam.
    // Evita mandar o token para /company, /shifts, chat, etc. (AuthGuard → 401 Invalid token).
    if (shareToken && onSharePage && !isSharePublicEndpoint && isShareAllowedApiUrl(req.url)) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + shareToken),
        });
        return next(newReq).pipe(
            catchError((error) => {
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 401 &&
                    error.url?.includes('/public/service-share/session/')
                ) {
                    sessionStorage.removeItem(SERVICE_SHARE_TOKEN_KEY);
                }
                return throwError(() => error);
            }),
        );
    }

    // Share token fora da página (ex.: bootstrap) — mesma restrição de rotas
    if (!isAuthenticated && shareToken && !isSharePublicEndpoint && isShareAllowedApiUrl(req.url)) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + shareToken),
        });
        return next(newReq).pipe(
            catchError((error) => throwError(() => error)),
        );
    }

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.

    if (isAuthenticated) {
        newReq = withDriverSimulationParam(
            req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authService.token),
            }),
        );
        // } else if (authService.token && authService.tokenRefresh && authService.rememberMe == "true") {
    } else if (authService.token && authService.tokenRefresh && !onSharePage) {
        let getEndPoint = newReq.url.split('/')[newReq.url.split('/').length - 1];

        if (getEndPoint.includes('refreshToken')) {
            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authService.tokenRefresh),
            });
        } else {
            return apiService.postSub('auth/refreshToken', {}).pipe(
                switchMap((res: { token: string; refreshToken: string }) => {
                    const { token, refreshToken } = res;

                    if (token) {
                        authService.token = token;
                    }

                    authService.tokenRefresh = refreshToken;

                    newReq = withDriverSimulationParam(
                        req.clone({
                            headers: req.headers.set('Authorization', 'Bearer ' + authService.token),
                        }),
                    );

                    return next(newReq).pipe(
                        catchError((error) => {
                            return erroHandle(error);
                        }),
                    );
                }),
                catchError((error) => {
                    return erroHandle(error);
                }),
            );
        }
    }

    // Response
    return next(newReq).pipe(
        map((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
                // if (event.headers.get('versao')) {
                //     versaoJWT = JSON.stringify(event.headers.get('versao'));
                //     const versao = localStorage.getItem(_storageKeysService.versao);
                //     if (versao && versao != event.headers.get('versao') && !newReq.url.includes('login')) {
                //         // this.staticshowNotificacaoVersao();
                //         localStorage.setItem(_storageKeysService.versao, event.headers.get('versao'));
                //         location.reload();
                //     } else if (!versao) {
                //         localStorage.setItem(_storageKeysService.versao, event.headers.get('versao'));
                //         location.reload();
                //     }
                // }
            }
            return event;
        }),
        catchError((error) => {
            return erroHandle(error);
        }),
    );
    function erroHandle(error) {
        // Na página de share, nunca força logout/reload
        if (onSharePage || getShareToken()) {
            return throwError(() => error);
        }

        // Catch "401 Unauthorized" responses
        if (
            error instanceof HttpErrorResponse &&
            ((error.status === 401 && !error.url?.includes('auth/login')) || !!error.url?.includes('refreshToken'))
        ) {
            if (isOnAuthPage()) {
                authService.logoutNotReload();
                return throwError(() => error);
            }

            authService.logout();
            location.reload();
        }

        return throwError(error);
    }
};
